// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, ClassId, MatrixCountry } from './types';
import { fmtValue, compact, money, delta, pctv, shortName } from './format';

export interface Insight {
  severity: 'info' | 'good' | 'warn' | 'alert';
  title: string;
  body: string;
  go?: { view: string; country?: string; cls?: ClassId; state?: string; label: string };
}

/** Countries present in a class, sorted by latest value desc. */
export function classCountries(d: Dataset, cid: ClassId) {
  return d.classes[cid].countries.slice().sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
}

/** The distinct set of #1 countries across all six classes (the "flip" set). */
export function topSet(d: Dataset): Set<string> {
  const s = new Set<string>();
  for (const cid of d.meta.classOrder) {
    const t = d.meta.counts.classTop[cid];
    if (t) s.add(t.name);
  }
  return s;
}

export function generateInsights(d: Dataset): Insight[] {
  const c = d.meta.counts;
  const out: Insight[] = [];
  const top = c.classTop;

  // 1. The flip
  const distinct = topSet(d);
  out.push({
    severity: 'info',
    title: `No single biggest foreign owner — it flips by asset`,
    body: `${distinct.size} different countries top the six asset classes: ${top.agricultural?.name && shortName(top.agricultural.name)} leads farmland, ${top.water && shortName(top.water.name)} water, ${top.residential && shortName(top.residential.name)} home purchases, ${top.commercial && shortName(top.commercial.name)} commercial property, ${top.business && shortName(top.business.name)} business interests and ${top.mining && shortName(top.mining.name)} mining tenements. "Who owns Australia" has no one answer.`,
    go: { view: 'atlas', label: 'Open the Atlas' },
  });

  // 2. China retreat
  if (c.agChinaDropPct != null && c.agChinaPeak && c.agChinaNow) {
    out.push({
      severity: 'good',
      title: `China is selling down Australian farmland`,
      body: `Chinese-controlled agricultural holdings peaked at ${compact(c.agChinaPeak)} ha and have fallen ${pctv(Math.abs(c.agChinaDropPct) * 100, 0)} to ${compact(c.agChinaNow)} ha — ceding the top spot to the ${shortName(c.agTop?.name ?? '')} (${compact(c.agTop?.value)} ha). The common belief that China is steadily buying up the farm runs the wrong way.`,
      go: { view: 'trends', label: 'See the farmland race' },
    });
  }

  // 3. Canada surge (water #1 + farmland doubling)
  const canada = d.matrix.countries.find((x) => x.iso === 'CA');
  if (canada?.byClass.agricultural && canada.agSeries) {
    const s = canada.agSeries.filter((v): v is number => v != null);
    if (s.length >= 3) {
      const recent = s[s.length - 1], twoPrior = s[s.length - 3];
      if (twoPrior && recent / twoPrior > 1.4) {
        out.push({
          severity: 'warn',
          title: `Canada's farmland stake jumped`,
          body: `Canadian-controlled agricultural land rose from ${compact(twoPrior)} ha to ${compact(recent)} ha in two years — a large single-jurisdiction increase, and Canada is also the largest foreign holder of Australian water (${fmtValue('water', top.water?.value)}).`,
          go: { view: 'atlas', country: 'Canada', label: 'Open Canada' },
        });
      }
    }
  }

  // 4. Tax havens
  const havens = d.matrix.countries.filter((x) => ['KY', 'VG', 'BM', 'LU', 'BS', 'MC', 'GG', 'JE'].includes(x.iso));
  const havenLead = top.business?.iso && ['KY', 'VG', 'BM'].includes(top.business.iso);
  if (havens.length) {
    out.push({
      severity: 'warn',
      title: `Fund domiciles loom large in the "control" data`,
      body: `${havens.length} of the source "countries" are low-tax fund domiciles (Cayman Islands, British Virgin Islands, Bermuda, Luxembourg${havenLead ? `, with ${shortName(top.business!.name)} the single largest source of registered business interests` : ''}). The ATO attributes each interest to where the controlling manager sits, not where the money ultimately comes from — so these reflect investment-fund structures.`,
      go: { view: 'explorer', label: 'Explore all sources' },
    });
  }

  // 5. Most foreign-owned state (farmland)
  const agStates = d.classes.agricultural.states.filter((s) => s.value != null).sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  if (agStates.length) {
    const t = agStates[0], lo = agStates[agStates.length - 1];
    out.push({
      severity: 'info',
      title: `The Northern Territory and Tasmania are the most foreign-owned`,
      body: `${pctv(t.value)} of ${t.code === 'NT' ? 'the Northern Territory' : t.code}'s agricultural land carries a foreign interest, against just ${pctv(lo.value)} in ${lo.code}. Nationally the figure is ${pctv(c.agForeignSharePct)} and has barely moved in a decade.`,
      go: { view: 'map', state: t.code, label: `Open ${t.code} on the map` },
    });
  }

  // 6. Residential cooldown + who buys
  const res = d.classes.residential;
  if (res.flow) {
    const ys = res.flow.years;
    const first = res.flow.totalsByYear[ys[0]], last = res.flow.totalsByYear[ys[ys.length - 1]];
    if (first && last) {
      out.push({
        severity: 'good',
        title: `Foreign home-buying has more than halved`,
        body: `New residential acquisitions by foreign persons fell from ${compact(first)} in ${ys[0]}–${String(ys[0] + 1).slice(2)} to ${compact(last)} in ${ys[ys.length - 1] - 1}–${String(ys[ys.length - 1]).slice(2)}. ${shortName(top.residential?.name ?? '')} remains the largest single source of buyers. Foreign non-residents can generally only buy new dwellings, not established homes.`,
        go: { view: 'assets', cls: 'residential', label: 'See residential detail' },
      });
    }
  }

  // 7. Commercial dollars
  if (c.commercialValueTotal && top.commercial) {
    out.push({
      severity: 'info',
      title: `Commercial property is where the money is`,
      body: `Foreign-registered commercial land is worth ${money(c.commercialValueTotal)}, and the ${shortName(top.commercial.name)} alone accounts for ${money(top.commercial.dollars)} across ${compact(top.commercial.value)} sites — far ahead of any other country. Retail and office property dominate the mix.`,
      go: { view: 'assets', cls: 'commercial', label: 'See commercial detail' },
    });
  }

  // 8. Biggest movers this year (matrix change)
  const movers: { name: string; cid: ClassId; chg: number }[] = [];
  for (const country of d.matrix.countries) {
    for (const cid of d.meta.classOrder) {
      const cell = country.byClass[cid];
      if (cell?.changePct != null && (cell.value ?? 0) > 0 && Math.abs(cell.changePct) > 0.5) {
        movers.push({ name: country.name, cid, chg: cell.changePct });
      }
    }
  }
  movers.sort((a, b) => Math.abs(b.chg) - Math.abs(a.chg));
  if (movers.length) {
    const m = movers[0];
    out.push({
      severity: m.chg > 0 ? 'warn' : 'good',
      title: `Biggest single-year move: ${shortName(m.name)} in ${d.meta.classMeta[m.cid].short.toLowerCase()}`,
      body: `${shortName(m.name)}'s registered ${d.meta.classMeta[m.cid].label.toLowerCase()} interests changed ${delta(m.chg, 0)} in the latest year. Year-on-year swings this large usually reflect a single large registration or divestment rather than a broad trend.`,
      go: { view: 'atlas', country: m.name, label: `Open ${shortName(m.name)}` },
    });
  }

  return out;
}

/** Rank helper used by drawers. */
export function rankIn(list: { name: string; value: number | null }[], name: string): string {
  const sorted = list.filter((x) => x.value != null).sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  const i = sorted.findIndex((x) => x.name === name);
  return i < 0 ? '—' : `${i + 1} of ${sorted.length}`;
}

export function findCountry(d: Dataset, name: string): MatrixCountry | undefined {
  return d.matrix.countries.find((c) => c.name === name);
}
